/**
 * Demo-session marker.
 *
 * `/demo` mints a shared read-only session on the sample "Rivertown Advisory"
 * site and stamps this flag so the app can frame the experience (persistent
 * "sample companies" banner with a signup path). The stored demo scenario is
 * matched to the current access token's session id, so a later normal login
 * cannot inherit stale demo UI state.
 */

const DEMO_MODE_KEY = 'fusegrid_demo_mode'
const DEMO_SCENARIO_KEY = 'fusegrid_demo_scenario'
const DEMO_PERSONA_KEY = 'fusegrid_demo_persona'
const DEMO_OWNER_HOME_KEY = 'fusegrid_demo_owner_home'

/** Where a demo session lands by default: the sample firm's cockpit. */
export const DEMO_ENTRY_PATH = '/rivertown-advisory/firm'

export function markDemoSession(sessionId?: string | null): void {
  try {
    const scenario = demoScenarioKeyFromSessionId(sessionId)
    if (!scenario) {
      window.localStorage.removeItem(DEMO_MODE_KEY)
      window.localStorage.removeItem(DEMO_SCENARIO_KEY)
      return
    }
    window.localStorage.setItem(DEMO_MODE_KEY, '1')
    window.localStorage.setItem(DEMO_SCENARIO_KEY, scenario)
  } catch {
    /* storage unavailable — banner just won't show */
  }
}

export function clearDemoSessionMarker(): void {
  try {
    window.localStorage.removeItem(DEMO_MODE_KEY)
    window.localStorage.removeItem(DEMO_SCENARIO_KEY)
    window.localStorage.removeItem(DEMO_PERSONA_KEY)
    window.localStorage.removeItem(DEMO_OWNER_HOME_KEY)
  } catch {
    /* ignore */
  }
}

export function demoScenarioKeyFromSessionId(
  sessionId: string | null | undefined,
): string | null {
  const compact = String(sessionId || '')
    .replace(/-/g, '')
    .toLowerCase()
  return /^[a-f0-9]{32}$/.test(compact)
    ? `demo_${compact.slice(0, 12)}`
    : null
}

export function demoScenarioKey(): string | null {
  try {
    const value = String(window.localStorage.getItem(DEMO_SCENARIO_KEY) || '')
      .trim()
      .toLowerCase()
    return /^demo_[a-f0-9]{12}$/.test(value) ? value : null
  } catch {
    return null
  }
}

export function isDemoSession(): boolean {
  try {
    if (window.localStorage.getItem(DEMO_MODE_KEY) !== '1') return false
    const markedScenario = demoScenarioKey()
    const accessToken = String(
      window.localStorage.getItem('fusegrid_api_token') || '',
    ).trim()
    const payload = accessToken.split('.')[1]
    if (!markedScenario || !payload) return false
    const normalized = payload.replace(/-/g, '+').replace(/_/g, '/')
    const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=')
    const claims = JSON.parse(atob(padded))
    const authenticatedScenario = demoScenarioKeyFromSessionId(claims?.sid)
    return authenticatedScenario === markedScenario
  } catch {
    return false
  }
}

// ---------------------------------------------------------------------------
// Demo module gate (lead capture)
//
// A demo visitor may open up to DEMO_FREE_MODULE_LIMIT distinct CFO modules
// for free; opening one more shows the lead gate, and contact info unlocks the
// rest of the session. State is per demo scenario in localStorage — this is
// lead capture, not a security boundary, so a cleared browser simply starts
// the allowance over.
// ---------------------------------------------------------------------------

/** How many distinct CFO modules a demo visitor may open before the gate. */
export const DEMO_FREE_MODULE_LIMIT = 3

/** Pages that never consume a free-module slot (landing + housekeeping). */
const DEMO_GATE_EXEMPT_PAGES = new Set(['home', 'settings', 'ceo'])

const demoVisitedStorageKey = (scenario: string): string =>
  `fusegrid_demo_visited_${scenario}`
const demoUnlockedStorageKey = (scenario: string): string =>
  `fusegrid_demo_unlocked_${scenario}`

export type DemoGateDecision = {
  gated: boolean
  /** Distinct gate-counted modules this visitor has opened, oldest first. */
  visited: string[]
}

export function readDemoVisitedModules(): string[] {
  const scenario = demoScenarioKey()
  if (!scenario) return []
  try {
    const parsed = JSON.parse(
      window.localStorage.getItem(demoVisitedStorageKey(scenario)) || '[]',
    )
    return Array.isArray(parsed)
      ? parsed.filter((page): page is string => typeof page === 'string').slice(0, 24)
      : []
  } catch {
    return []
  }
}

export function isDemoGateUnlocked(): boolean {
  const scenario = demoScenarioKey()
  if (!scenario) return false
  try {
    return window.localStorage.getItem(demoUnlockedStorageKey(scenario)) === '1'
  } catch {
    return false
  }
}

export function unlockDemoGate(): void {
  const scenario = demoScenarioKey()
  if (!scenario) return
  try {
    window.localStorage.setItem(demoUnlockedStorageKey(scenario), '1')
  } catch {
    /* unlock still applies for this render via component state */
  }
}

/**
 * Decide whether the current demo visitor may open `page`, recording the
 * visit when it fits inside the free allowance. Anything outside a demo
 * session — or an exempt/already-visited/unlocked page — passes through.
 */
/**
 * A guided tour owns the visitor's attention until it ends.
 *
 * The module-count gate exists to catch a wanderer at the third page. Firing it
 * mid-narration is a hard stop in the middle of a sentence — the tour makes its
 * own ask at the end, once the visitor has seen the whole story. Set while a
 * tour is running; in memory only, so a reload without a tour behaves normally.
 */
let tourGateSuppressed = false

export function setTourGateSuppressed(suppressed: boolean): void {
  tourGateSuppressed = suppressed
}

export function isTourGateSuppressed(): boolean {
  return tourGateSuppressed
}

export function claimDemoModule(page: string): DemoGateDecision {
  const visited = readDemoVisitedModules()
  const scenario = demoScenarioKey()
  const normalized = String(page || '').trim().toLowerCase()
  if (
    tourGateSuppressed ||
    !scenario ||
    !normalized ||
    DEMO_GATE_EXEMPT_PAGES.has(normalized) ||
    !isDemoSession() ||
    isDemoGateUnlocked() ||
    visited.includes(normalized)
  ) {
    return { gated: false, visited }
  }
  if (visited.length < DEMO_FREE_MODULE_LIMIT) {
    const next = [...visited, normalized]
    try {
      window.localStorage.setItem(
        demoVisitedStorageKey(scenario),
        JSON.stringify(next),
      )
    } catch {
      /* allowance just resets if storage is unavailable */
    }
    return { gated: false, visited: next }
  }
  return { gated: true, visited }
}

/** Last module opened inside the allowance — where "keep exploring" returns. */
export function lastVisitedDemoModule(): string | null {
  const visited = readDemoVisitedModules()
  return visited.length ? visited[visited.length - 1] : null
}

/**
 * Demo sessions never own the account-level CFO experiences selector. If a
 * demo visitor reaches a selector URL (including the bare root), send them
 * back to the sample firm's cockpit instead of mounting the selector.
 */
export function demoHomeRedirectPath(hasApiToken: boolean): string | null {
  if (!(hasApiToken && isDemoSession())) return null
  return demoEntryPathForSession()
}

export type DemoPersona = 'owner' | 'firm'

export function setDemoPersona(persona: DemoPersona): void {
  try {
    window.localStorage.setItem(DEMO_PERSONA_KEY, persona)
  } catch {
    /* ignore */
  }
}

export function getDemoPersona(): DemoPersona | null {
  try {
    const value = String(window.localStorage.getItem(DEMO_PERSONA_KEY) || '').trim().toLowerCase()
    return value === 'owner' || value === 'firm' ? value : null
  } catch {
    return null
  }
}

export function setDemoOwnerHome(path: string): void {
  const trimmed = String(path || '').trim()
  if (!trimmed.startsWith('/') || trimmed.startsWith('//')) return
  try {
    window.localStorage.setItem(DEMO_OWNER_HOME_KEY, trimmed)
  } catch {
    /* ignore */
  }
}

export function getDemoOwnerHome(): string | null {
  try {
    const value = String(window.localStorage.getItem(DEMO_OWNER_HOME_KEY) || '').trim()
    return value.startsWith('/') && !value.startsWith('//') ? value : null
  } catch {
    return null
  }
}

export function demoEntryPathForSession(): string {
  if (getDemoPersona() === 'owner') {
    return getDemoOwnerHome() || DEMO_ENTRY_PATH
  }
  return DEMO_ENTRY_PATH
}

export function defaultDemoCfoPage(): 'ceo' | 'home' {
  return getDemoPersona() === 'owner' ? 'ceo' : 'home'
}

/**
 * Validate a `?to=` deep-link target from the marketing site: same-origin
 * absolute paths only (no protocol-relative or external URLs).
 */
export function sanitizeDemoRedirect(raw: string | null | undefined): string {
  const value = String(raw || '').trim()
  if (!value.startsWith('/') || value.startsWith('//')) {
    return demoEntryPathForSession()
  }
  return value
}
